import React, { memo, useCallback, useContext, useEffect, useMemo } from "react";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import {
    AnalyticsContext,
    ObjectNames,
    ResponseContext,
    ScreenNames,
    TableContext,
    useDigitalMenuConfig,
} from "@swiggy-private/portal-dineout-context";
import { BILL_TIMEOUT_TEXTS, DEFAULT_TEXTS } from "./constant";
import {
    Container,
    ErrorContainer,
    ErrorPageWrapper,
    PendingAndErrorHeaderContainer,
    Separator,
    SeparatorButtonText,
    SeparatorText,
} from "./styles";
import { BILL_SUMMARY_SCREEN_HEADER } from "../constants/track";
import { ErrorComponent } from "../ErrorComponent";
import { getV2ConfigFlags } from "../utils";

interface BillErrorPageProps {
    onErrorRetry: VoidFunction;
}

const BillErrorPage = ({ onErrorRetry }: BillErrorPageProps) => {
    const { state } = useContext(ResponseContext);
    const { errorInfo } = state?.billSummary || {};
    const { restaurantId, tableId, parentOrderId } = useContext(TableContext);
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);
    const { config } = useDigitalMenuConfig();
    const { isV2, isV2InApp } = useMemo(() => getV2ConfigFlags(config), [config]);

    const handleErrorRetry = useCallback(() => {
        clickEvent({
            category: ScreenNames.BILL_SUMMARY,
            action: ObjectNames.CLICK_BILL_SUMMARY_ERROR_RETRY,
            label: BILL_TIMEOUT_TEXTS.CTA_TEXT,
            context: JSON.stringify({
                restaurant_id: restaurantId,
                table_id: tableId,
                parentOrderId,
                table_no: state?.pin?.data?.tableNo,
                is_v2_config: isV2,
                theme: isV2InApp ? config?.themeInfo?.theme : undefined,
            }),
        });

        onErrorRetry?.();
    }, [
        clickEvent,
        config?.themeInfo?.theme,
        isV2,
        isV2InApp,
        onErrorRetry,
        parentOrderId,
        restaurantId,
        tableId,
        state?.pin?.data?.tableNo,
    ]);

    useEffect(() => {
        impressionEvent({
            category: ScreenNames.BILL_SUMMARY,
            action: ObjectNames.IMPRESSION_BILL_SUMMARY_ERROR,
            label: errorInfo?.title || DEFAULT_TEXTS.TITLE,
            context: JSON.stringify({
                restaurant_id: restaurantId,
                table_id: tableId,
                parentOrderId,
                table_no: state?.pin?.data?.tableNo,
                isPollingTimedOut: errorInfo?.isPollingTimedOut,
            }),
        });
    }, [
        impressionEvent,
        errorInfo?.title,
        errorInfo?.isPollingTimedOut,
        restaurantId,
        tableId,
        parentOrderId,
        state?.pin?.data?.tableNo,
    ]);

    return (
        <Container>
            <PendingAndErrorHeaderContainer>
                <Text
                    variant="V2.body2.neutral.bold"
                    textAlign="center"
                    color="text_Highest_Emphasis"
                    as="span"
                >
                    {BILL_SUMMARY_SCREEN_HEADER}
                </Text>
            </PendingAndErrorHeaderContainer>

            <ErrorPageWrapper>
                <ErrorContainer>
                    <ErrorComponent
                        heading={errorInfo?.title || DEFAULT_TEXTS.TITLE}
                        description={errorInfo?.message || DEFAULT_TEXTS.DESCRIPTION}
                        descriptionColor="text_Med_Emphasis"
                        imgSrc={errorInfo?.imgUrl || DEFAULT_TEXTS.IMG_URL}
                        ctaBtnType={"primary"}
                        imgWidth="auto"
                        showFullPage={false}
                        onCtaClick={handleErrorRetry}
                        ctaText={DEFAULT_TEXTS.CTA_TEXT}
                        showCta={!errorInfo?.isPollingTimedOut}
                        imgHeight={!errorInfo?.isPollingTimedOut ? "128px" : undefined}
                    />
                    {errorInfo?.isPollingTimedOut && (
                        <>
                            <Separator />
                            <SeparatorText
                                variant="V2.body2.neutral.regular"
                                color="text_Low_Emphasis"
                            >
                                {BILL_TIMEOUT_TEXTS.TITLE}
                            </SeparatorText>
                            <SuperButton
                                buttonType="primary"
                                buttonSize="small"
                                onClick={handleErrorRetry}
                            >
                                <SeparatorButtonText
                                    variant="V2.body2.neutral.bold"
                                    color="text_Button_Primary"
                                >
                                    {BILL_TIMEOUT_TEXTS.CTA_TEXT}
                                </SeparatorButtonText>
                            </SuperButton>
                        </>
                    )}
                </ErrorContainer>
            </ErrorPageWrapper>
        </Container>
    );
};

export default memo(BillErrorPage);
